// src/pages/Dashboard.js
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { db } from "../firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  onSnapshot,
} from "firebase/firestore";
import useAuth from "../hooks/useAuth";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

/* ----------------- helpers ----------------- */

const normalizeDate = (ts) => {
  if (!ts) return null;
  const raw =
    typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
  // strip time so timezone can’t shift it
  return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
};

const dayKey = (d) => d.getTime(); // number ms since epoch, good for sorting

const dayLabel = (d) =>
  d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const computeStreak = (dateSet) => {
  const days = Array.from(dateSet).sort((a, b) => a - b); // numbers (ms)
  if (days.length === 0) return 0;

  let streak = 1;
  let current = days[days.length - 1];

  for (let i = days.length - 2; i >= 0; i--) {
    const diffDays = (current - days[i]) / (1000 * 60 * 60 * 24);
    if (diffDays === 1) {
      streak += 1;
      current = days[i];
    } else {
      break;
    }
  }
  return streak;
};

export default function Dashboard() {
  const { user } = useAuth();

  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
    totalGoals: 0,
    completedGoals: 0,
    journalEntries: 0,
    goalStreak: 0,
    journalStreak: 0,
    activeStreak: 0,
  });

  const [badgeInfo, setBadgeInfo] = useState({
    badges: null,
    featuredBadge: null,
    displayName: "",
  });

  const [goalChart, setGoalChart] = useState([]);
  const [journalChart, setJournalChart] = useState([]);
  const [moodChart, setMoodChart] = useState([]);
  const [achievements, setAchievements] = useState([]);

  const reflectionPrompts = [
    "What is one small win you are proud of today?",
    "What challenged you today and what did you learn from it?",
    "What is one thing you handled better than last week?",
    "What thought or habit do you want to leave behind?",
    "What are three things you are grateful for right now?",
    "What is one thing your future self would thank you for doing today?",
  ];

  const todayPrompt =
    reflectionPrompts[new Date().getDate() % reflectionPrompts.length];

  const moodColors = ["#22c55e", "#3b82f6", "#a855f7", "#f97316", "#eab308"];

  /* ------------- live user info (premium + featured badge) ------------- */
  useEffect(() => {
    if (!user) return;

    const userRef = doc(db, "users", user.uid);
    const unsub = onSnapshot(userRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();

      setIsPremium(!!data.premium);

      const badges = data.badges || {};
      const featuredBadge = data.featuredBadge || null;

      setBadgeInfo({
        badges,
        featuredBadge,
        displayName: data.displayName || "",
      });
    });

    return () => unsub();
  }, [user]);

  /* ------------- load goals + journals (stats + charts) ------------- */
  useEffect(() => {
    if (!user) return;

    const load = async () => {
      try {
        setLoading(true);

        // GOALS
        const goalsQuery = query(
          collection(db, "goals"),
          where("uid", "==", user.uid)
        );
        const goalsSnap = await getDocs(goalsQuery);
        const goals = goalsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        const totalGoals = goals.length;
        const completedGoals = goals.filter((g) => g.done);

        // group completed goals by date
        const goalsByDate = {};
        const goalDatesSet = new Set();

        completedGoals.forEach((g) => {
          const ts = g.completedAt || g.createdAt;
          if (!ts) return;
          const d = normalizeDate(ts);
          if (!d) return;
          const key = dayKey(d);
          goalsByDate[key] = (goalsByDate[key] || 0) + 1;
          goalDatesSet.add(key);
        });

        const goalChartData = Object.entries(goalsByDate)
          .sort((a, b) => Number(a[0]) - Number(b[0]))
          .map(([ms, count]) => {
            const d = new Date(Number(ms));
            return { date: dayLabel(d), count };
          });

        setGoalChart(goalChartData);

        // JOURNALS
        const journalQueryRef = query(
          collection(db, "journal"),
          where("uid", "==", user.uid)
        );
        const journalSnap = await getDocs(journalQueryRef);
        const journals = journalSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        const journalEntries = journals.length;

        const journalsByDate = {};
        const journalDatesSet = new Set();
        const moodCounts = {};

        journals.forEach((j) => {
          const ts = j.createdAt;
          if (!ts) return;
          const d = normalizeDate(ts);
          if (!d) return;
          const key = dayKey(d);
          journalsByDate[key] = (journalsByDate[key] || 0) + 1;
          journalDatesSet.add(key);

          const mood = j.mood || "🙂";
          moodCounts[mood] = (moodCounts[mood] || 0) + 1;
        });

        const journalChartData = Object.entries(journalsByDate)
          .sort((a, b) => Number(a[0]) - Number(b[0]))
          .map(([ms, entries]) => {
            const d = new Date(Number(ms));
            return { date: dayLabel(d), entries };
          });

        setJournalChart(journalChartData);

        const moodData = Object.entries(moodCounts).map(
          ([name, value]) => ({ name, value })
        );
        setMoodChart(moodData);

        // streaks (computed from date sets)
        const goalStreak = computeStreak(goalDatesSet);
        const journalStreak = computeStreak(journalDatesSet);
        const activeStreak = Math.max(goalStreak, journalStreak);

        setStats({
          totalGoals,
          completedGoals: completedGoals.length,
          journalEntries,
          goalStreak,
          journalStreak,
          activeStreak,
        });
      } catch (err) {
        console.error("Error loading dashboard data", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user]);

  /* ------------- achievements derived from real stats ------------- */
  useEffect(() => {
    const list = [];

    if (stats.completedGoals >= 5) {
      list.push("You have completed 5 or more goals. Nice work.");
    }
    if (stats.journalEntries >= 5) {
      list.push("Your journal habit is growing nicely.");
    }
    if (stats.goalStreak >= 3 || stats.journalStreak >= 3) {
      list.push("You are building a strong streak. Keep going.");
    }

    if (list.length === 0) {
      list.push("Keep using ReflectRise and your achievements will show here.");
    }

    setAchievements(list);
  }, [stats]);

  if (!user) {
    return (
      <div className="home-center">
        <h2>Dashboard</h2>
        <p>Please sign in to view your dashboard.</p>
      </div>
    );
  }

  // Premium lock
  if (!isPremium && !loading) {
    return (
      <>
        <Helmet>
          <title>Premium Dashboard - ReflectRise</title>
        </Helmet>
        <div
          className="card premium-locked"
          style={{
            background: "var(--card)",
            color: "var(--text)",
            textAlign: "center",
            padding: "2rem",
            borderRadius: "12px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.5)",
            maxWidth: "600px",
            margin: "3rem auto",
          }}
        >
          <h2 style={{ color: "var(--primary)", marginBottom: "1rem" }}>
            🔒 Premium Dashboard
          </h2>
          <p style={{ marginBottom: "0.5rem" }}>
            The dashboard with advanced analytics is a premium feature.
          </p>
          <p>
            Upgrade to ReflectRise Premium to unlock streak charts, stats and
            insights.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard - ReflectRise</title>
      </Helmet>

      <div className="content" style={{ maxWidth: "1100px", margin: "0 auto" }}>
        {/* Top row: snapshot + featured badge */}
        <div
          className="dashboard-grid"
          style={{
            gridTemplateColumns: "minmax(0, 2.2fr) minmax(0, 1.8fr)",
            alignItems: "stretch",
          }}
        >
          {/* Snapshot card */}
          <div
            className="card dashboard-card"
            style={{
              background: "var(--card)",
              borderRadius: "16px",
              padding: "1.8rem",
              boxShadow: "0 8px 20px rgba(0,0,0,0.45)",
            }}
          >
            <h2 style={{ marginBottom: "0.5rem", color: "var(--primary)" }}>
              Welcome back
              {badgeInfo.displayName ? `, ${badgeInfo.displayName}` : ""} 👋
            </h2>
            <p style={{ marginBottom: "1.2rem", color: "var(--muted)" }}>
              Visual overview of your goals, streaks and reflection habits.
            </p>

            <div className="dashboard-grid" style={{ marginTop: "0.5rem" }}>
              <div className="stat-box">
                <h3>{stats.completedGoals}</h3>
                <p>Goals completed</p>
              </div>
              <div className="stat-box">
                <h3>{stats.journalEntries}</h3>
                <p>Journal entries</p>
              </div>
              <div className="stat-box">
                <h3>{stats.activeStreak}</h3>
                <p>Longest streak</p>
              </div>
            </div>

            <div
              style={{
                marginTop: "1.5rem",
                padding: "1rem",
                borderRadius: "12px",
                background:
                  "linear-gradient(135deg, rgba(168,85,247,0.3), rgba(56,189,248,0.25))",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "0.95rem",
                  color: "#e5e7eb",
                }}
              >
                🧠 Reflection prompt of the day:
              </p>
              <p
                style={{
                  margin: "0.4rem 0 0",
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: "#ffffff",
                }}
              >
                {todayPrompt}
              </p>
            </div>
          </div>

          {/* Featured badge */}
          <div
            className="card dashboard-card"
            style={{
              background: "var(--card)",
              borderRadius: "16px",
              padding: "1.6rem",
              boxShadow: "0 8px 20px rgba(0,0,0,0.45)",
            }}
          >
            <h3 style={{ color: "var(--primary)", marginBottom: "0.8rem" }}>
              Featured badge
            </h3>
            {badgeInfo.featuredBadge ? (
              <div
                style={{
                  borderRadius: "14px",
                  padding: "1rem 1.2rem",
                  background:
                    "radial-gradient(circle at top left, rgba(250,204,21,0.25), transparent 55%), #111827",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.35rem",
                }}
              >
                <span style={{ fontSize: "2rem" }}>
                  {badgeInfo.featuredBadge.emoji || "🏅"}
                </span>
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "1.05rem",
                    color: "#f9fafb",
                  }}
                >
                  {badgeInfo.featuredBadge.label || "Achievement unlocked"}
                </span>
                <span style={{ fontSize: "0.9rem", color: "var(--muted)" }}>
                  Category: {badgeInfo.featuredBadge.category || "general"}
                </span>
                {typeof badgeInfo.featuredBadge.value === "number" && (
                  <span style={{ fontSize: "0.9rem", color: "var(--muted)" }}>
                    Value: {badgeInfo.featuredBadge.value}
                  </span>
                )}
              </div>
            ) : (
              <p style={{ color: "var(--muted)" }}>
                You have not selected a featured badge yet.
              </p>
            )}

            <div style={{ marginTop: "1.2rem", fontSize: "0.9rem" }}>
              <p style={{ color: "var(--muted)", marginBottom: "0.4rem" }}>
                Your current badge progress:
              </p>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  color: "var(--text)",
                }}
              >
                <li>🎯 Goal streak: {stats.goalStreak} days</li>
                <li>📝 Journal streak: {stats.journalStreak} days</li>
                <li>✅ Goal total: {stats.completedGoals}</li>
                <li>📓 Journal total: {stats.journalEntries}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Recent achievements only (middle row) */}
        <div className="dashboard-grid">
          <div
            className="card dashboard-card"
            style={{
              background: "var(--card)",
              borderRadius: "16px",
              padding: "1.5rem",
              boxShadow: "0 6px 18px rgba(0,0,0,0.4)",
            }}
          >
            <h3 style={{ color: "var(--primary)", marginBottom: "0.8rem" }}>
              Recent achievements
            </h3>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                color: "var(--text)",
                fontSize: "0.95rem",
              }}
            >
              {achievements.map((msg, idx) => (
                <li
                  key={idx}
                  style={{
                    marginBottom: "0.6rem",
                    padding: "0.6rem 0.7rem",
                    borderRadius: "10px",
                    background: "rgba(148, 163, 184, 0.08)",
                  }}
                >
                  🌟 {msg}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Charts row */}
        <div className="dashboard-grid">
          {/* Goal progress over time */}
          <div
            className="card dashboard-card"
            style={{
              background: "var(--card)",
              borderRadius: "16px",
              padding: "1.5rem",
              boxShadow: "0 6px 18px rgba(0,0,0,0.4)",
            }}
          >
            <h3 style={{ color: "var(--primary)", marginBottom: "1rem" }}>
              🎯 Goal progress over time
            </h3>
            {goalChart.length > 0 ? (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={goalChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2933" />
                    <XAxis dataKey="date" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" allowDecimals={false} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#a855f7"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p style={{ color: "var(--muted)" }}>No goals yet.</p>
            )}
          </div>

          {/* Journal activity over time */}
          <div
            className="card dashboard-card"
            style={{
              background: "var(--card)",
              borderRadius: "16px",
              padding: "1.5rem",
              boxShadow: "0 6px 18px rgba(0,0,0,0.4)",
            }}
          >
            <h3 style={{ color: "var(--primary)", marginBottom: "1rem" }}>
              📝 Journal activity over time
            </h3>
            {journalChart.length > 0 ? (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={journalChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2933" />
                    <XAxis dataKey="date" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" allowDecimals={false} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="entries"
                      stroke="#38bdf8"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p style={{ color: "var(--muted)" }}>No journal entries yet.</p>
            )}
          </div>
        </div>

        {/* Mood distribution */}
        <div
          className="card dashboard-card"
          style={{
            background: "var(--card)",
            borderRadius: "16px",
            padding: "1.5rem",
            boxShadow: "0 6px 18px rgba(0,0,0,0.4)",
            marginBottom: "2.5rem",
          }}
        >
          <h3 style={{ color: "var(--primary)", marginBottom: "1rem" }}>
            😊 Mood distribution
          </h3>
          {moodChart.length > 0 ? (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={moodChart}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label
                  >
                    {moodChart.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={moodColors[index % moodColors.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p style={{ color: "var(--muted)" }}>No mood data yet.</p>
          )}
        </div>
      </div>
    </>
  );
}
